import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Receipt, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ACTIVITY_TYPES_TAX, PME_REGIMES, numOr0, computeTax } from '../lib/taxHelpers';
import TaxResultCard from '../components/TaxResultCard';
import TaxBaremeInfo from '../components/TaxBaremeInfo';

const YESNO = [
  { id: 'no', label: 'Non' },
  { id: 'yes', label: 'Oui' },
];

export const ImpotsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activityType, setActivityType] = useState('');
  const [hasLiberatoire, setHasLiberatoire] = useState('no');
  const [hasOtherIncome, setHasOtherIncome] = useState('no');
  const [otherIncomeAmount, setOtherIncomeAmount] = useState('');
  const [annualCA, setAnnualCA] = useState('');
  const [pmeRegime, setPmeRegime] = useState('');
  const [pmeBenefice, setPmeBenefice] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get('/tax/profile');
        const p = r.data || {};
        if (p.activity_type) setActivityType(p.activity_type);
        setHasLiberatoire(p.has_liberatoire ? 'yes' : 'no');
        setHasOtherIncome(p.has_other_income ? 'yes' : 'no');
        if (p.other_income_amount) setOtherIncomeAmount(String(p.other_income_amount));
        if (p.annual_ca) setAnnualCA(String(p.annual_ca));
        if (p.pme_regime) setPmeRegime(p.pme_regime);
        if (p.pme_benefice) setPmeBenefice(String(p.pme_benefice));
      } catch (e) {
        // first-time
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPme = activityType === 'pme';
  const isPmeIR = isPme && pmeRegime === 'ir';

  const ca = useMemo(() => numOr0(annualCA), [annualCA]);
  const benefice = useMemo(() => numOr0(pmeBenefice), [pmeBenefice]);
  const otherIncome = useMemo(
    () => (hasOtherIncome === 'yes' ? numOr0(otherIncomeAmount) : 0),
    [hasOtherIncome, otherIncomeAmount],
  );
  const result = useMemo(
    () => computeTax(activityType, ca, hasLiberatoire === 'yes', otherIncome, pmeRegime, benefice),
    [activityType, ca, hasLiberatoire, otherIncome, pmeRegime, benefice],
  );

  const handleSave = async () => {
    if (!activityType) {
      toast.error("Veuillez sélectionner un type d'activité");
      return;
    }
    if (isPme && !pmeRegime) {
      toast.error('Veuillez sélectionner un régime fiscal (IS ou IR)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/tax/profile', {
        activity_type: activityType,
        has_liberatoire: hasLiberatoire === 'yes',
        has_other_income: hasOtherIncome === 'yes',
        other_income_amount: otherIncome,
        annual_ca: ca,
        pme_regime: isPme ? pmeRegime : null,
        pme_benefice: isPme ? benefice : 0,
      });
      toast.success('Profil fiscal enregistré');
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const cards = result ? result.cards : [];
  const showOtherIncome = !isPme || isPmeIR;

  return (
    <div className="space-y-6" data-testid="impots-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="impots-page-title">
          Impôts
        </h1>
        <p className="text-slate-500 text-sm md:text-base">
          Estimez votre impôt selon votre activité (auto-entrepreneur ou PME) et le régime fiscal applicable.
        </p>
      </div>

      <Card className="border border-slate-200" data-testid="impots-form-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-outfit text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#065F46]" />
            Renseignez votre situation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {loading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="tax-activity" className="text-sm text-slate-700">
                    Type d'activité
                  </Label>
                  <Select value={activityType} onValueChange={setActivityType}>
                    <SelectTrigger id="tax-activity" data-testid="tax-activity-select">
                      <SelectValue placeholder="Choisissez votre type d'activité" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_TYPES_TAX.map((t) => (
                        <SelectItem key={t.id} value={t.id} data-testid={`tax-activity-option-${t.id}`}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isPme ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="tax-pme-regime" className="text-sm text-slate-700">
                      Régime fiscal
                    </Label>
                    <Select value={pmeRegime} onValueChange={setPmeRegime}>
                      <SelectTrigger id="tax-pme-regime" data-testid="tax-pme-regime-select">
                        <SelectValue placeholder="IS ou IR" />
                      </SelectTrigger>
                      <SelectContent>
                        {PME_REGIMES.map((r) => (
                          <SelectItem key={r.id} value={r.id} data-testid={`tax-pme-regime-option-${r.id}`}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {isPme ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="tax-pme-benefice" className="text-sm text-slate-700">
                      Bénéfice annuel (€)
                    </Label>
                    <Input
                      id="tax-pme-benefice"
                      type="number"
                      min="0"
                      step="any"
                      value={pmeBenefice}
                      onChange={(e) => setPmeBenefice(e.target.value)}
                      className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      data-testid="tax-pme-benefice-input"
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="tax-ca" className="text-sm text-slate-700">
                      CA annuel (€)
                    </Label>
                    <Input
                      id="tax-ca"
                      type="number"
                      min="0"
                      step="any"
                      value={annualCA}
                      onChange={(e) => setAnnualCA(e.target.value)}
                      className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      data-testid="tax-ca-input"
                    />
                  </div>
                )}

                {!isPme ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="tax-lib" className="text-sm text-slate-700">
                      Prélèvement libératoire
                    </Label>
                    <Select value={hasLiberatoire} onValueChange={setHasLiberatoire}>
                      <SelectTrigger id="tax-lib" data-testid="tax-liberatoire-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YESNO.map((y) => (
                          <SelectItem key={y.id} value={y.id} data-testid={`tax-liberatoire-${y.id}`}>
                            {y.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {showOtherIncome ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="tax-other" className="text-sm text-slate-700">
                      Autres revenus
                    </Label>
                    <Select value={hasOtherIncome} onValueChange={setHasOtherIncome}>
                      <SelectTrigger id="tax-other" data-testid="tax-other-income-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YESNO.map((y) => (
                          <SelectItem key={y.id} value={y.id} data-testid={`tax-other-${y.id}`}>
                            {y.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>

              {showOtherIncome && hasOtherIncome === 'yes' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="tax-other-amount" className="text-sm text-slate-700">
                    Montant des autres revenus (€)
                  </Label>
                  <Input
                    id="tax-other-amount"
                    type="number"
                    min="0"
                    step="any"
                    value={otherIncomeAmount}
                    onChange={(e) => setOtherIncomeAmount(e.target.value)}
                    className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46] md:w-72"
                    data-testid="tax-other-amount-input"
                  />
                </div>
              ) : null}

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#065F46] hover:bg-[#047857] text-white"
                  data-testid="save-tax-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {result && cards.length > 0 ? (
        <div
          className={`grid gap-4 ${cards.length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}
          data-testid="tax-results"
        >
          {cards.map((card, idx) => (
            <TaxResultCard
              key={idx}
              idx={idx}
              title={card.title}
              tone={card.tone}
              total={card.total}
              lines={card.breakdown}
            />
          ))}
        </div>
      ) : null}

      <TaxBaremeInfo />
    </div>
  );
};

export default ImpotsPage;
